import React, { FC, memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useI18n, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import Loader from '~ComponentsImproved/Loader';
import loyaltyIcon from '~Assets/icons/loyalty.svg';
import checkIcon from '~Assets/icons/check-circle.svg';
import closeIcon from '~Assets/icons/close.svg';
import { useExpressLoyalty } from '../../../../Providers/ExpressLoyaltyProvider';
import StepFrame from '../../../Checkout/StepFrame';
import ChoiceButton from '../../../Checkout/ChoiceButton';
import Modal, { MODAL_TRANSITION_MS } from '../../../../Components/Modal';

/** How long "Welcome back" stays up before the modal closes itself. */
const SUCCESS_LINGER_MS = 1800;

/** The card eases between the screens' heights - same curve as the card's entrance. */
const RESIZE_MS = 320;

/** What the modal is showing. */
type Phase = 'prompting' | 'member' | 'unknown' | 'timeout';

/** Same entrance/exit pattern as ClearCartConfirm: keyframe in, CSSTransition-driven out. */
/** The card: scales and fades in, and back out. StepFrame provides its own padding. */
const Card = styled.div`
  position: relative;
  width: min(100%, 860px);
  border-radius: 36px;
  background: var(--eposSurface);
  color: var(--eposText);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.45);
  /* Clips the next screen while the height eases to it. */
  overflow: hidden;
  transform: translate3d(0, 0, 0) scale(1);
  opacity: 1;
  animation: eposLoyaltyCardIn ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity ${MODAL_TRANSITION_MS}ms ease-out;
  will-change: transform, opacity;

  .epos-loyalty-exit-active &,
  .epos-loyalty-exit-done & {
    animation: none;
    transform: translate3d(0, 24px, 0) scale(0.96);
    opacity: 0;
  }

  @keyframes eposLoyaltyCardIn {
    from {
      transform: translate3d(0, 24px, 0) scale(0.96);
      opacity: 0;
    }
    to {
      transform: translate3d(0, 0, 0) scale(1);
      opacity: 1;
    }
  }
`;

/** The icon on a padded plate - same box as the order number on DoneStep. */
const IconPlate = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 56px;
  border-radius: 32px;
  background: var(--colorBlueGray100);
`;

/** The found member's name, under the welcome line. */
const MemberName = styled.div`
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.6px;
`;

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Asks the terminal for the shopper's card as soon as it opens. A member
 * closes it by itself after a short welcome; an unknown card or no card
 * within the timeout shows what happened and offers another try. Closing
 * while prompting aborts the prompt on the terminal.
 */
const LoyaltyModal: FC<Props> = ({ open, onClose }) => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-loyalty-modal');
  const expressLoyalty = useExpressLoyalty();

  const [phase, setPhase] = useState<Phase>('prompting');
  const [memberName, setMemberName] = useState<string | null>(null);
  /** Guards results that come back after the modal was closed. */
  const openRef = useRef(open);
  openRef.current = open;
  /** Read in the close cleanup - a found member's card stays with the cart for the payment. */
  const phaseRef = useRef<Phase>('prompting');
  phaseRef.current = phase;
  const cardRef = useRef<HTMLDivElement>(null);
  /** The card's height on the previous screen, null right after opening. */
  const lastHeightRef = useRef<number | null>(null);
  /** The height animation still playing, if any - a quick screen change takes over from it. */
  const resizeRef = useRef<Animation | null>(null);

  /** Before the first paint on open, so the last session's screen never flashes. */
  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    /** Already on the prompt (closed mid-prompt): no re-render follows, so measure it here. */
    lastHeightRef.current =
      phaseRef.current === 'prompting' ? cardRef.current?.offsetHeight ?? null : null;
    setPhase('prompting');
    setMemberName(null);
  }, [open]);

  /**
   * Each screen change eases the card from the old height to the new one - one
   * measure per change, then the browser animates it. Without this the
   * centered card snaps at both edges.
   */
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) {
      return;
    }
    /** Mid-animation the card is at its animated height - start from there, not the old target. */
    const running = resizeRef.current;
    const from = running ? card.offsetHeight : lastHeightRef.current;
    running?.cancel();
    resizeRef.current = null;
    const to = card.offsetHeight;
    lastHeightRef.current = to;
    if (from === null || from === to || typeof card.animate !== 'function') {
      return;
    }
    const resize = card.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: RESIZE_MS,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    });
    resize.onfinish = () => {
      if (resizeRef.current === resize) {
        resizeRef.current = null;
      }
    };
    resizeRef.current = resize;
  }, [phase]);

  /** One prompt; the outcome decides the next screen. */
  const prompt = useCallback(async () => {
    setPhase('prompting');
    const result = await expressLoyalty.identify();
    if (!openRef.current) {
      return;
    }
    logger.info('Loyalty prompt finished in the cart modal', { outcome: result.status });
    if (result.status === 'member') {
      setMemberName(result.memberName);
      setPhase('member');
    } else if (result.status === 'unknown') {
      setPhase('unknown');
    } else if (result.status === 'timeout') {
      setPhase('timeout');
    } else {
      // Deliberately stopped elsewhere - nothing to show, the modal just goes away.
      onClose();
    }
  }, [expressLoyalty, onClose]);

  // Prompt on open; abort the terminal if closed while still asking. A member's
  // card is never released here - the payment takes it over.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    prompt();
    return () => {
      if (phaseRef.current === 'prompting') {
        expressLoyalty.release();
      }
    };
  }, [open]);

  // A found member: linger on the welcome, then close by itself.
  useEffect(() => {
    if (phase !== 'member') {
      return undefined;
    }
    const timer = window.setTimeout(onClose, SUCCESS_LINGER_MS);
    return () => window.clearTimeout(timer);
  }, [phase, onClose]);

  return (
    <Modal
      open={open}
      exitPrefix="epos-loyalty"
      onBackdropTap={onClose}
      testId="epos-loyalty-modal">
      <Card ref={cardRef}>
        {/* Keyed per screen so StepFrame remounts and its slide-in plays on every change. */}
        {phase === 'member' ? (
          <StepFrame
            key="member"
            testId="epos-loyalty-modal-member"
            title={i18n('Express.Loyalty.Modal.Welcome')}>
            <IconPlate>
              <Icon src={checkIcon} color="var(--eposButton)" width="96px" height="96px" />
            </IconPlate>
            {!!memberName && <MemberName>{memberName}</MemberName>}
          </StepFrame>
        ) : phase === 'unknown' || phase === 'timeout' ? (
          <StepFrame
            key={phase}
            testId={`epos-loyalty-modal-${phase}`}
            title={i18n(
              phase === 'timeout'
                ? 'Express.Loyalty.Modal.Timeout'
                : 'Express.Loyalty.Modal.NotFound',
            )}
            subtitle={i18n(
              phase === 'timeout'
                ? 'Express.Loyalty.Modal.Timeout.Body'
                : 'Express.Loyalty.Modal.NotFound.Body',
            )}
            actions={
              <>
                <ChoiceButton
                  label={i18n('General.Cancel')}
                  icon={closeIcon}
                  onClick={onClose}
                  testId="epos-loyalty-modal-cancel"
                />
                <ChoiceButton
                  primary
                  label={i18n('General.Retry')}
                  icon={loyaltyIcon}
                  onClick={prompt}
                  testId="epos-loyalty-modal-retry"
                />
              </>
            }
          />
        ) : (
          <StepFrame
            key="prompting"
            testId="epos-loyalty-modal-prompting"
            title={i18n('Express.Loyalty.Modal.Title')}
            subtitle={i18n('Express.Loyalty.Modal.SubTitle')}>
            <IconPlate>
              <Icon src={loyaltyIcon} color="var(--eposText)" width="96px" height="96px" />
            </IconPlate>
            <Loader size="sm" color="var(--eposText)" />
          </StepFrame>
        )}
      </Card>
    </Modal>
  );
};

export default memo(LoyaltyModal);
